import { createRouter, createWebHistory } from 'vue-router'
import { useUserStore } from '@/stores/user'

const mainChildren = [
  {
    path: '',
    name: 'Home',
    component: () => import('@/views/home/HomeDashboard.vue'),
    meta: { title: '首页', requiresAuth: true }
  },
  {
    path: 'notice',
    name: 'NoticeList',
    component: () => import('@/views/notice/NoticeList.vue'),
    meta: { title: '社区通知', requiresAuth: true }
  },
  {
    path: 'notice/detail/:id',
    name: 'NoticeDetail',
    component: () => import('@/views/notice/NoticeDetail.vue'),
    meta: { title: '通知详情', requiresAuth: true }
  },
  {
    path: 'notice/manage',
    name: 'NoticeManage',
    component: () => import('@/views/notice/NoticeManage.vue'),
    meta: { title: '公告管理', requiresAuth: true, requiresAdmin: true }
  },
  {
    path: 'admin/user',
    name: 'UserManage',
    component: () => import('@/views/admin/UserManage.vue'),
    meta: { title: '用户管理', requiresAuth: true, requiresAdmin: true }
  },
  {
    path: 'item',
    name: 'ItemList',
    component: () => import('@/views/item/ItemList.vue'),
    meta: { title: '闲置物品', requiresAuth: true }
  },
  {
    path: 'item/publish',
    name: 'ItemPublish',
    component: () => import('@/views/item/ItemPublish.vue'),
    meta: { title: '发布物品', requiresAuth: true }
  },
  {
    path: 'item/detail/:id',
    name: 'ItemDetail',
    component: () => import('@/views/item/ItemDetail.vue'),
    meta: { title: '物品详情', requiresAuth: true }
  },
  {
    path: 'item/edit/:id',
    name: 'ItemEdit',
    component: () => import('@/views/item/ItemEdit.vue'),
    meta: { title: '编辑物品', requiresAuth: true }
  },
  {
    path: 'item/my',
    name: 'MyItems',
    component: () => import('@/views/item/MyItems.vue'),
    meta: { title: '我的发布', requiresAuth: true }
  },
  {
    path: 'item/audit',
    name: 'ItemAudit',
    component: () => import('@/views/item/ItemAudit.vue'),
    meta: { title: '物品审核', requiresAuth: true, requiresAdmin: true }
  },
  {
    path: 'collection',
    name: 'CollectionIndex',
    component: () => import('@/views/collection/CollectionIndex.vue'),
    meta: { title: '我的收藏', requiresAuth: true }
  },
  {
    path: 'profile',
    name: 'UserProfile',
    component: () => import('@/views/user/UserProfile.vue'),
    meta: { title: '个人中心', requiresAuth: true }
  },
  {
    path: 'service',
    name: 'ServiceList',
    component: () => import('@/views/service/ServiceList.vue'),
    meta: { title: '便民服务', requiresAuth: true }
  },
  {
    path: 'service/detail/:id',
    name: 'ServiceDetail',
    component: () => import('@/views/service/ServiceDetail.vue'),
    meta: { title: '服务商详情', requiresAuth: true }
  },
  {
    path: 'service/apply',
    name: 'ServiceApply',
    component: () => import('@/views/service/ServiceApply.vue'),
    meta: { title: '服务商入驻', requiresAuth: true }
  },
  {
    path: 'service/collection',
    name: 'ServiceCollection',
    component: () => import('@/views/service/ServiceCollection.vue'),
    meta: { title: '服务收藏', requiresAuth: true }
  },
  {
    path: 'service/my',
    name: 'ServiceMy',
    component: () => import('@/views/service/ServiceMy.vue'),
    meta: { title: '我的服务商', requiresAuth: true }
  },
  {
    path: 'service/edit',
    name: 'ServiceEdit',
    component: () => import('@/views/service/ServiceEdit.vue'),
    meta: { title: '服务商管理', requiresAuth: true }
  },
  {
    path: 'service/audit',
    name: 'ServiceAudit',
    component: () => import('@/views/service/ServiceAudit.vue'),
    meta: { title: '服务商审核', requiresAuth: true, requiresAdmin: true }
  }
]

const routes = [
  {
    path: '/login',
    name: 'Login',
    component: () => import('@/views/Login.vue'),
    meta: { title: '登录', requiresAuth: false }
  },
  {
    path: '/register',
    name: 'Register',
    component: () => import('@/views/Register.vue'),
    meta: { title: '注册', requiresAuth: false }
  },
  {
    path: '/',
    component: () => import('@/layouts/MainLayout.vue'),
    meta: { requiresAuth: true },
    children: mainChildren
  }
]

const router = createRouter({
  history: createWebHistory(),
  routes
})

const adminOnlyMenuPaths = [
  '/notice/manage',
  '/admin/user',
  '/item/audit',
  '/service/audit',
  '/profile',
  '/'
]

const isAdminRestrictedPath = (path) => {
  const restrictedPrefixes = [
    '/notice',
    '/item',
    '/collection',
    '/service'
  ]
  if (adminOnlyMenuPaths.includes(path)) {
    return false
  }
  if (path.startsWith('/notice') && path !== '/notice/manage') {
    return true
  }
  if (path.startsWith('/item/detail/')) {
    return false
  }
  if (path.startsWith('/item') && path !== '/item/audit') {
    return true
  }
  if (path.startsWith('/service') && path !== '/service/audit') {
    return true
  }
  if (path.startsWith('/collection')) {
    return true
  }
  if (path.startsWith('/admin') && path !== '/admin/user') {
    return true
  }
  return restrictedPrefixes.some(prefix => path.startsWith(prefix)) && !adminOnlyMenuPaths.includes(path)
}

function resolveRouteTitle(to) {
  const withTitle = [...to.matched].reverse().find((r) => r.meta?.title)
  return withTitle?.meta?.title
}

router.beforeEach((to, from, next) => {
  const title = resolveRouteTitle(to)
  document.title = title ? `${title} - 社区便民服务平台` : '社区便民服务平台'

  const userStore = useUserStore()
  const isLoggedIn = userStore.isLoggedIn
  const role = userStore.role

  const requiresAuth = to.matched.some((r) => r.meta.requiresAuth)
  const requiresAdmin = to.matched.some((r) => r.meta.requiresAdmin)

  if (requiresAuth && !isLoggedIn) {
    next('/login')
  } else if ((to.path === '/login' || to.path === '/register') && isLoggedIn) {
    next('/')
  } else if (requiresAdmin && role !== 'admin') {
    next('/notice')
  } else if (role === 'admin' && isAdminRestrictedPath(to.path)) {
    next('/notice/manage')
  } else {
    next()
  }
})

export default router
